/**
 * Sensitivity analysis — ±20% levers on Items, Balance waves, and ARPU.
 * Pure functions; does not mutate the input scenario.
 */

import { projectGrowthPath } from './growthProjection';

const SENSITIVITY_MULTIPLIERS = [0.8, 1, 1.2];

function cloneScenario(scenario) {
  return JSON.parse(JSON.stringify(scenario));
}

function deterministicScenario(scenario) {
  return {
    ...scenario,
    growth: {
      ...scenario.growth,
      model: 'deterministic',
      distribution: 'fixed',
    },
  };
}

/**
 * @param {object} scenario
 * @returns {{ horizonCogs: number, netGm: number, revenue: number }}
 */
export function horizonMetrics(scenario) {
  const months = projectGrowthPath(deterministicScenario(scenario), null);
  const last = months[months.length - 1];
  const horizonCogs = last.plaidCogs + last.stripeFees + last.aiCogs;
  // Auth is a one-time acquisition cost, not recurring gross-margin COGS.
  const netGm = last.revenue - horizonCogs;
  return {
    horizonCogs,
    netGm,
    revenue: last.revenue,
  };
}

function scaleProfileItems(scenario, multiplier) {
  const next = cloneScenario(scenario);
  next.profiles = next.profiles.map((profile) => ({
    ...profile,
    items: Math.max(0, profile.items * multiplier),
  }));
  return next;
}

function scaleProfileBalanceWaves(scenario, multiplier) {
  const next = cloneScenario(scenario);
  next.profiles = next.profiles.map((profile) => ({
    ...profile,
    balanceWaves: Math.max(0, profile.balanceWaves * multiplier),
  }));
  return next;
}

function scalePlanArpu(scenario, multiplier) {
  const next = cloneScenario(scenario);
  next.plans = Object.fromEntries(
    Object.entries(next.plans).map(([slug, plan]) => [
      slug,
      { ...plan, monthly: Math.max(0, plan.monthly * multiplier) },
    ])
  );
  return next;
}

const LEVERS = [
  {
    id: 'items',
    label: 'Items',
    apply: scaleProfileItems,
  },
  {
    id: 'balanceWaves',
    label: 'Balance waves',
    apply: scaleProfileBalanceWaves,
  },
  {
    id: 'arpu',
    label: 'ARPU (plan monthly)',
    apply: scalePlanArpu,
  },
];

/**
 * @param {object} scenario
 * @returns {Array<{ id: string, label: string, low: object, base: object, high: object }>}
 */
export function computeSensitivityTable(scenario) {
  const base = horizonMetrics(scenario);

  return LEVERS.map((lever) => {
    const [lowMult, , highMult] = SENSITIVITY_MULTIPLIERS;
    const low = horizonMetrics(lever.apply(scenario, lowMult));
    const high = horizonMetrics(lever.apply(scenario, highMult));

    return {
      id: lever.id,
      label: lever.label,
      low: {
        multiplier: lowMult,
        horizonCogs: low.horizonCogs,
        netGm: low.netGm,
        deltaCogs: low.horizonCogs - base.horizonCogs,
        deltaNetGm: low.netGm - base.netGm,
      },
      base,
      high: {
        multiplier: highMult,
        horizonCogs: high.horizonCogs,
        netGm: high.netGm,
        deltaCogs: high.horizonCogs - base.horizonCogs,
        deltaNetGm: high.netGm - base.netGm,
      },
    };
  });
}
